import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Wifi, CheckCircle2, Coffee, WifiOff, ArrowRight, PhoneMissed,
  ArrowDownLeft, ArrowUpRight, Clock, ArrowRightLeft,
} from 'lucide-react';
import clsx from 'clsx';
import { useLeaderAgents, STATUS_DISPLAY } from '@/lib/useLeaderAgents';
import { useAuth } from '@/store/auth';
import { fmtHHMMSS } from '@/lib/useTodayStats';
import { CallDonut, DonutLegend } from '@/components/workspace/CallDonut';
import { LiveCallsBoard } from '@/components/leader/LiveCallsBoard';
import type { LeaderAgentRow } from '@/lib/api';

/**
 * Takım Lideri ANA SAYFASI (Yönetim Paneli). Önceden lider de agent Workspace'ini
 * görüyordu (kritik açık) — artık takım GENEL durumu: anlık durum dağılımı +
 * takım-geneli bugünkü çağrı toplamları + dikkat gerektirenler. Veri useLeaderAgents
 * (3CX canlı durum + activity_logs günlük metrik, 15s). Detay → Temsilciler.
 */
const BREAK_STATUSES = ['mola', 'yemek', 'dis-arama', 'ozel'];

export default function LeaderDashboard() {
  const { user } = useAuth();
  const { data: agents, isLoading, isError } = useLeaderAgents();
  const list = useMemo(() => agents ?? [], [agents]);

  const agg = useMemo(() => {
    const sum = (f: (a: LeaderAgentRow) => number) => list.reduce((s, a) => s + f(a), 0);
    return {
      total: list.length,
      online: list.filter((a) => a.online).length,
      kuyrukta: list.filter((a) => a.queueLoggedIn).length,
      hazir: list.filter((a) => a.status === 'hazir').length,
      mola: list.filter((a) => BREAK_STATUSES.includes(a.status)).length,
      cevrimdisi: list.filter((a) => !a.online).length,
      answered: sum((a) => a.daily.callsAnswered),
      missed: sum((a) => a.daily.callsMissed),
      outbound: sum((a) => a.daily.callsOutbound),
      talkSec: sum((a) => a.daily.totalTalkSec),
      transfers: sum((a) => a.daily.transfers),
      breaks: sum((a) => a.daily.breakCount),
    };
  }, [list]);

  const onBreak = useMemo(() => list.filter((a) => BREAK_STATUSES.includes(a.status)), [list]);
  const missedAgents = useMemo(
    () => list.filter((a) => a.daily.callsMissed > 0).sort((a, b) => b.daily.callsMissed - a.daily.callsMissed).slice(0, 8),
    [list],
  );

  const firstName = (user?.display_name ?? '').split(' ')[0] || user?.username || '';
  const donutAnswered = { label: 'Cevap', value: agg.answered, colorClass: 'text-emerald-500' };
  const donutMissed = { label: 'Cevapsız', value: agg.missed, colorClass: 'text-red-500' };
  const donutOutbound = { label: 'Giden', value: agg.outbound, colorClass: 'text-amber-500' };

  return (
    <div className="px-5 py-3 max-w-[1300px] mx-auto">
      {/* Header */}
      <header className="flex items-end justify-between mb-2.5 gap-3">
        <div>
          <h1 className="text-[18px] font-semibold tracking-tight text-slate-900 leading-tight">
            {greeting()} <strong className="text-brand-600">{firstName}</strong> 👋
          </h1>
          <div className="text-[11.5px] text-slate-400 mt-0.5">Takım genel durumu — bugün</div>
        </div>
        <Link
          to="/agents"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-brand-500 hover:bg-brand-600 text-[#08140d] shadow-[0_4px_12px_-3px_rgba(255,79,31,0.5)] transition"
        >
          <Users size={13} /> Temsilciler <ArrowRight size={13} />
        </Link>
      </header>

      {isError && (
        <div className="text-[12.5px] text-red-600 bg-red-50 ring-1 ring-red-200 rounded-xl px-4 py-3 mb-2.5">
          Takım verisi alınamadı — 3CX bağlantısını kontrol et.
        </div>
      )}

      {/* Durum dağılımı — 6 tile */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2.5 mb-2.5">
        <StatusTile icon={<Users size={15} />}        tone="slate"   label="Toplam"      value={agg.total} loading={isLoading} />
        <StatusTile icon={<Wifi size={15} />}         tone="emerald" label="Çevrimiçi"   value={agg.online} loading={isLoading} />
        <StatusTile icon={<CheckCircle2 size={15} />} tone="emerald" label="Hazır"       value={agg.hazir} loading={isLoading} />
        <StatusTile icon={<Wifi size={15} />}         tone="sky"     label="Kuyrukta"    value={agg.kuyrukta} loading={isLoading} />
        <StatusTile icon={<Coffee size={15} />}       tone="amber"   label="Molada"      value={agg.mola} loading={isLoading} />
        <StatusTile icon={<WifiOff size={15} />}      tone="rose"    label="Çevrimdışı"  value={agg.cevrimdisi} loading={isLoading} />
      </div>

      {/* Şu an konuşanlar — canlı çağrı board'u (supervisor monitoring) */}
      <div className="mb-2.5">
        <LiveCallsBoard />
      </div>

      {/* Takım-geneli bugünkü çağrılar: donut + KPI */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-2.5 mb-2.5">
        <section className="lg:col-span-2 rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 flex flex-col items-center justify-center shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 self-start mb-1">Takım Çağrı Dağılımı</div>
          <CallDonut segments={[donutAnswered, donutMissed]} size={128} />
          <DonutLegend segments={[donutAnswered, donutMissed, donutOutbound]} />
        </section>

        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          <Kpi icon={<ArrowDownLeft size={14} />}  tone="emerald" label="Cevaplanan"     value={agg.answered} />
          <Kpi icon={<PhoneMissed size={14} />}    tone="red"     label="Cevapsız"       value={agg.missed} />
          <Kpi icon={<ArrowUpRight size={14} />}   tone="amber"   label="Giden"          value={agg.outbound} />
          <Kpi icon={<Clock size={14} />}          tone="brand"   label="Toplam Görüşme" value={fmtHHMMSS(agg.talkSec)} mono />
          <Kpi icon={<ArrowRightLeft size={14} />} tone="slate"   label="Aktarım"        value={agg.transfers} />
          <Kpi icon={<Coffee size={14} />}         tone="slate"   label="Mola"           value={agg.breaks} />
        </div>
      </div>

      {/* Alt: Şu an molada + Dikkat (cevapsız) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        <AgentMiniList
          title="Şu an molada" icon={<Coffee size={13} />} empty="Molada temsilci yok"
          rows={onBreak.map((a) => ({ a, right: <StatusChip a={a} /> }))}
        />
        <AgentMiniList
          title="Bugün cevapsız (dikkat)" icon={<PhoneMissed size={13} />} empty="Cevapsız çağrı yok 🎉"
          rows={missedAgents.map((a) => ({
            a,
            right: <span className="text-[12px] font-bold text-rose-600 tabular-nums">{a.daily.callsMissed} cevapsız</span>,
          }))}
        />
      </div>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return 'Günaydın';
  if (h >= 11 && h < 17) return 'İyi günler';
  if (h >= 17 && h < 22) return 'İyi akşamlar';
  return 'İyi geceler';
}

/* ─── Tiles / KPI / list ──────────────────────────────────────── */

function StatusTile({ icon, tone, label, value, loading }: {
  icon: React.ReactNode; tone: 'slate' | 'emerald' | 'sky' | 'amber' | 'rose';
  label: string; value: number; loading?: boolean;
}) {
  const iconCls = {
    slate: 'bg-slate-100 text-slate-600', emerald: 'bg-emerald-50 text-emerald-600',
    sky: 'bg-sky-50 text-sky-600', amber: 'bg-amber-50 text-amber-600', rose: 'bg-rose-50 text-rose-600',
  }[tone];
  return (
    <div className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm flex items-center gap-2.5">
      <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', iconCls)}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none">{loading ? '–' : value}</div>
        <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mt-0.5 truncate">{label}</div>
      </div>
    </div>
  );
}

function Kpi({ icon, tone, label, value, mono }: {
  icon: React.ReactNode; tone: 'emerald' | 'red' | 'amber' | 'brand' | 'slate';
  label: string; value: React.ReactNode; mono?: boolean;
}) {
  const iconCls = {
    emerald: 'bg-emerald-50 text-emerald-600', red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600', brand: 'bg-brand-50 text-brand-600', slate: 'bg-slate-100 text-slate-600',
  }[tone];
  return (
    <div className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm flex items-center gap-2.5">
      <div className={clsx('w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0', iconCls)}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] font-medium text-slate-400 uppercase tracking-widest truncate">{label}</div>
        <div className={clsx('text-[18px] font-bold text-slate-900 tabular-nums leading-tight', mono && 'font-mono text-[15px]')}>{value}</div>
      </div>
    </div>
  );
}

function StatusChip({ a }: { a: LeaderAgentRow }) {
  const sd = STATUS_DISPLAY[a.status];
  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ring-1 text-[10.5px] font-medium', sd.chip)}>
      <span className={clsx('w-1.5 h-1.5 rounded-full', sd.dot)} />
      {sd.label}{a.status === 'ozel' && a.profileMessage ? ` · ${a.profileMessage}` : ''}
    </span>
  );
}

function AgentMiniList({ title, icon, rows, empty }: {
  title: string; icon: React.ReactNode; empty: string;
  rows: { a: LeaderAgentRow; right: React.ReactNode }[];
}) {
  return (
    <section className="bg-surface border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      <header className="flex items-center gap-2 px-3.5 py-2 border-b border-slate-100 text-[12.5px] font-medium text-slate-900">
        <span className="w-[20px] h-[20px] rounded-md bg-brand-100 text-brand-700 flex items-center justify-center">{icon}</span>
        {title}
        <span className="ml-auto text-[11px] text-slate-400 tabular-nums">{rows.length}</span>
      </header>
      <div className="p-2">
        {rows.length === 0 ? (
          <div className="py-6 text-center text-[11.5px] text-slate-400">{empty}</div>
        ) : (
          <div className="flex flex-col">
            {rows.map(({ a, right }) => (
              <div key={a.id} className="flex items-center gap-2.5 px-1.5 py-1.5 border-b border-slate-50 last:border-0">
                <div className="relative w-7 h-7 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 text-white flex items-center justify-center text-[10px] font-semibold shrink-0">
                  {a.displayName.slice(0, 2).toUpperCase()}
                  <span className={clsx('absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-base', a.online ? 'bg-emerald-500' : 'bg-slate-300')} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium text-slate-900 truncate leading-tight">{a.displayName}</div>
                  <div className="text-[10.5px] text-slate-400 font-mono">{a.extension}</div>
                </div>
                <div className="flex-shrink-0">{right}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
